import InfoIcon from '@material-ui/icons/InfoOutlined'
import { osLiteSupportLink } from 'constants/links'
import { ComponentVersions_3_0 } from 'constants/uxVersions'
import { adaNotificationSelectors } from 'ducks/adaNotification'
import { authSelectors } from 'ducks/auth'
import { rolesSelectors } from 'ducks/auth_roles'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { viewModeSelectors } from 'ducks/viewMode'
import { FeatureSettingsButton } from 'layout/widgets/FeatureSettingsButton'
import { BaseThemeType, ComponentVersionsInherit, MenuItem, PopoutMenu, styled, Typography } from 'opensolar-ui'
import { useLocale, useTranslate } from 'ra-core'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { OpenSolarTheme } from 'Themes'
import { getHelpCenterByLocale, getRoleIdFromState } from 'util/misc'
import AdaReviewModal from './AdaReviewModal'
import NavigationAccountButton, { MissingRole } from './NavigationAccountButton'

const NavSettingsMenuItem = styled(MenuItem)({
  // Top Nav Menu width: 208px, MenuItem margins: 8px horizontally
  // 220px is the width of the menu item in the new design
  width: 'calc(220px - 16px)',
})

const InfoMenuItem = styled(NavSettingsMenuItem)({
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  display: 'flex',
  alignItems: 'flex-start',
  '& p:not(:first-child)': {
    marginTop: 4,
  },
})

const StyledInfoMenuWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row',
})

interface NavigationSettingsMenuProps {
  showOrgSelector(): void
}

const NavigationSettingsMenu: React.FC<NavigationSettingsMenuProps> = ({ showOrgSelector }) => {
  const isUserLite = useSelector(viewModeSelectors.isUserLite)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const orgs = useSelector(authSelectors.getBriefOrgs) || []
  const org = useSelector(orgSelectors.getOrg)
  const history = useHistory()
  const locale = useLocale()
  const translate = useTranslate()
  const roleId = useSelector(getRoleIdFromState)

  const roleComplete: boolean = useSelector(rolesSelectors.getIsRoleComplete)
  const { allowView } = useSelector((state) => permissionsSelectors.getPermissionByKey(state, 'team'))
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const hasAdaPendingReview = useSelector(adaNotificationSelectors.getHasPendingReview) && isAdmin

  const [adaModalOpen, setAdaModalOpen] = useState(false)

  const showIncompleteRoleWarning = !roleComplete && allowView

  const handleRequestClose = (to, label, navState = {}) => {
    history.push({
      pathname: to,
      state: {
        confirmNavigationHeading: translate('Leave Project to %{label}?', { label }),
        ...navState,
      },
    })
  }

  if (!org) return null

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <PopoutMenu
        anchorEl={<NavigationAccountButton logoPublicUrl={org.logo_public_url} />}
        menuId="nav-account-context"
        menuProps={{
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          transformOrigin: { horizontal: 'right', vertical: 'top' },
          keepMounted: true,
        }}
      >
        {hasAdaPendingReview ? (
          <InfoMenuItem divider onClick={() => setAdaModalOpen(true)}>
            <StyledInfoMenuWrapper>
              <InfoIcon
                style={{
                  color: OpenSolarTheme.blue4 as `#${string}`,
                  width: 20,
                  height: 20,
                  marginRight: 4,
                }}
              />
              <AdaNotificationContent>
                <Typography textVariant="caption1" colorHex={OpenSolarTheme.greyDark2 as `#${string}`}>
                  {translate('Ada found information for your business')}
                </Typography>
                <Typography textVariant="caption1" colorHex={OpenSolarTheme.greyMid1 as `#${string}`}>
                  {translate("Review and confirm before it's added to your account.")}
                </Typography>
                <Typography textVariant="caption1" colorHex={OpenSolarTheme.blue4 as `#${string}`}>
                  <u>{translate('Review suggestions')}</u>
                </Typography>
              </AdaNotificationContent>
            </StyledInfoMenuWrapper>
          </InfoMenuItem>
        ) : (
          (false as const)
        )}
        {showIncompleteRoleWarning && (
          <InfoMenuItem
            divider
            onClick={() => {
              handleRequestClose('/roles/' + roleId, translate('Complete my Details'))
            }}
          >
            <StyledInfoMenuWrapper>
              <InfoIcon
                style={{
                  color: OpenSolarTheme.blue4 as `#${string}`,
                  width: 20,
                  height: 20,
                  marginRight: 4,
                }}
              />
              <MissingRole />
            </StyledInfoMenuWrapper>
          </InfoMenuItem>
        )}

        <NavSettingsMenuItem
          divider
          onClick={() => {
            handleRequestClose('/configuration', translate('Settings'))
          }}
        >
          {translate('Settings')}
        </NavSettingsMenuItem>
        {(isStaff || orgs.length > 1) && (
          <NavSettingsMenuItem
            onClick={() => {
              // Handled by the global navigation block now, see LeaveProjectPrompt
              // if (window.projectForm?.mutators.getFormDirtyFields().length > 0) {
              //   const result = window.confirm('Any unsaved data will be lost. Are you sure?')
              //   if (!result) return
              // }
              showOrgSelector()
            }}
          >
            {translate('Select Organisation')}
          </NavSettingsMenuItem>
        )}
        <FeatureSettingsButton MenuItemComponent={NavSettingsMenuItem} />
        <NavSettingsMenuItem
          divider
          onClick={() => {
            isUserLite ? window.open(osLiteSupportLink, '_blank') : window.open(getHelpCenterByLocale(locale), '_blank')
          }}
        >
          {translate('Help Center')}
        </NavSettingsMenuItem>
        <NavSettingsMenuItem
          color="destructive"
          onClick={() => {
            handleRequestClose('/logout', translate('Logout'), { reason: 'user-settings-dropdown' })
          }}
          sx={(theme: BaseThemeType) => ({ color: theme.palette.red[900] })}
        >
          {translate('Logout')}
        </NavSettingsMenuItem>
      </PopoutMenu>

      <AdaReviewModal open={adaModalOpen} onClose={() => setAdaModalOpen(false)} />
    </ComponentVersionsInherit>
  )
}

export default NavigationSettingsMenu

const AdaNotificationContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})
